import { useState } from 'react'
import { ChevronDown, ChevronRight, Play } from 'lucide-react'
import { useTranslation } from '@/i18n'
import { IntuiButton } from '@/components/intueri/IntuiButton'
import type { Plan } from '../v4/contracts'
import type { PlanCard } from '../v4/contracts/card-types'
import { stepAction } from '../v4/step-label'
import { AGENT_NAME } from './agentName'
import AgentDot from './AgentDot'
import { MethodFilePick, type SendFile } from './AskCard'
import { planSourceKey, type PlanSourceKey } from './runStory'

const PLAN_FROM: Record<PlanSourceKey, string> = {
  plan_from_catalog: 'from procedure {{procedure}}',
  plan_from_file: 'from the supplied file',
  plan_from_interview: 'from the interview',
  plan_from_question: 'from your steps',
  plan_from_data: 'worked out from the data',
}

/** What the plan card says about a part of the question: its status, as a locale key and its English. */
const PART_WORDS: Record<string, [string, string]> = {
  answered: ['part_answered', 'answered by the steps below'],
  asked: ['part_asked', 'asked of you'],
  cant: ['part_cant', "can't be answered"],
  filler: ['part_filler', 'no meaning, left out'],
  purpose: ['part_purpose', 'your reason, the answer speaks to it'],
}

/** Where a value came from, as the plan card marks it. */
const ORIGIN_WORDS: Record<PlanCard['values'][number]['source'], [string, string]> = {
  yours: ['origin_yours', 'your words'],
  answer: ['origin_answer', 'your answer'],
  data: ['origin_data', 'from the data'],
  document: ['origin_document', 'from a document'],
  reading: ['origin_reading', 'my reading, please check'],
}

/**
 * "AI Plant Support Engineer: Plan v1 ready" (the old chat's "Done Creating Workflow" card, Figma
 * "8. Report"): a collapsible plain-language summary of the engine's plan (its steps in order, how
 * it read the question, the values it filled in and where they came from, the reviewer's flags)
 * and, while this version awaits approval, the Run box ("Your workflow is ready. Do you want to
 * run it?") with the method-file door beside it. The newest plan opens; older versions start
 * collapsed.
 */
export default function PlanReadyCard({
  plan,
  card,
  current,
  awaiting,
  continues = null,
  busy,
  onRun,
  fileEnabled,
  onFile,
}: {
  plan: Plan
  card: PlanCard | null
  /** This is the run's current plan version. */
  current: boolean
  /** The run waits for this version's approval: show the Run box. */
  awaiting: boolean
  /** An earlier version already ran: this one carries the run on, so the Run box says so, and why. */
  continues?: 'found' | 'edited' | null
  busy: boolean
  /** Approves the plan (the same action as the first canvas node's Run pill). */
  onRun: () => void
  /** A method file can still shape the plan. */
  fileEnabled: boolean
  onFile: SendFile
}) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(current)
  const Chevron = open ? ChevronDown : ChevronRight
  const asks = plan.steps.filter((s) => s.kind === 'ask').length
  const sourceKey = planSourceKey(plan)
  const fromKey: string = `workflow:builder.${sourceKey}`
  const from = String(t(fromKey, { defaultValue: PLAN_FROM[sourceKey], procedure: plan.source.procedureId ?? '' }))
  const details = card && (card.flags.length > 0 || card.parts.length > 0 || card.values.length > 0)

  return (
    <div>
      {/* Muted header: orange is reserved for what is in progress (the old chat's rule). */}
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-2 text-left text-sm font-medium text-muted-foreground"
      >
        <AgentDot />
        <span>
          {AGENT_NAME}: {t('workflow:builder.plan_ready', { defaultValue: 'Plan v{{version}} ready', version: plan.version })}
        </span>
        <Chevron className="size-4 shrink-0" />
      </button>
      {open && (
        <div className="mt-2 space-y-2 text-sm leading-relaxed text-foreground">
          <p>
            {t('workflow:builder.plan_summary', {
              defaultValue: 'Plan v{{version}} has {{count}} step(s), {{from}}.',
              version: plan.version,
              count: plan.steps.length,
              from,
            })}
          </p>
          <ol className="list-decimal space-y-1 pl-5">
            {plan.steps.map((s) => (
              <li key={s.id}>
                {s.title} <span className="text-xs text-muted-foreground">· {stepAction(s)}</span>
              </li>
            ))}
          </ol>
          {asks > 0 && (
            <p className="text-muted-foreground">
              {t('workflow:builder.plan_asks', {
                defaultValue: '{{count}} step(s) can ask you for input; the rest run straight through.',
                count: asks,
              })}
            </p>
          )}
          {plan.reason && (
            <p className="text-muted-foreground">
              {t('workflow:builder.plan_reason', { defaultValue: 'Why this version: {{reason}}', reason: plan.reason })}
            </p>
          )}
          {details && (
            <div className="space-y-2 rounded-lg border border-border bg-muted/30 p-3 text-xs dark:bg-dark-muted/30">
              {card.flags.map((f) => (
                <p key={`${f.at}|${f.problem}`} className="text-amber-700 dark:text-amber-400">
                  <b>
                    {t('workflow:builder.plan_reviewer', { defaultValue: 'Reviewer' })} ({f.at}):
                  </b>{' '}
                  {f.problem}
                </p>
              ))}
              {card.parts.length > 0 && (
                <div>
                  <p className="font-medium text-foreground">
                    {t('workflow:builder.plan_read', { defaultValue: 'How I read your question' })}
                  </p>
                  <ul className="mt-1 space-y-0.5">
                    {card.parts.map((p) => {
                      const [key, fallback] = PART_WORDS[p.status] ?? ['', p.status]
                      const partKey: string = `workflow:builder.${key}`
                      return (
                        <li key={p.id}>
                          <span className="text-foreground">“{p.quote}”</span>{' '}
                          <span className="text-muted-foreground">
                            · {key ? String(t(partKey, { defaultValue: fallback })) : fallback}
                            {p.note ? `: ${p.note}` : ''}
                          </span>
                        </li>
                      )
                    })}
                  </ul>
                </div>
              )}
              {card.values.length > 0 && (
                <div>
                  <p className="font-medium text-foreground">{t('workflow:builder.plan_values', { defaultValue: 'Values' })}</p>
                  <ul className="mt-1 space-y-0.5">
                    {card.values.map((v) => {
                      const [key, fallback] = ORIGIN_WORDS[v.source]
                      const originKey: string = `workflow:builder.${key}`
                      return (
                        <li key={v.name}>
                          <span className="font-mono">{v.name}</span> = <b className="text-foreground">{v.shown}</b>{' '}
                          <span className={v.source === 'reading' ? 'text-amber-700 dark:text-amber-400' : 'text-muted-foreground'}>
                            · {String(t(originKey, { defaultValue: fallback }))}
                          </span>
                          {v.source === 'reading' && v.why ? <span className="text-muted-foreground"> · {v.why}</span> : null}
                        </li>
                      )
                    })}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
      )}
      {awaiting && (
        <div className="mt-2 rounded-lg border border-border bg-muted/30 p-3 dark:bg-dark-muted/30">
          {continues ? (
            <>
              <p className="text-sm text-foreground">
                {continues === 'edited'
                  ? t('workflow:builder.plan_continue_edited', {
                      defaultValue:
                        'This is your edited plan, version {{version}}. The steps already done are kept: approve it to carry on from there.',
                      version: plan.version,
                    })
                  : t('workflow:builder.plan_continue_question', {
                      defaultValue:
                        'While the workflow ran, a step found something that changed the plan, so I wrote version {{version}}. The steps already done are kept: approve this version to carry on from there.',
                      version: plan.version,
                    })}
              </p>
              {continues === 'found' && plan.reason && (
                <p className="mt-1 text-sm text-muted-foreground">
                  {t('workflow:builder.plan_continue_reason', { defaultValue: 'What changed: {{reason}}', reason: plan.reason })}
                </p>
              )}
            </>
          ) : (
            <p className="text-sm text-foreground">
              {t('workflow:builder.plan_run_question', { defaultValue: 'Your workflow is ready. Do you want to run it?' })}
            </p>
          )}
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <IntuiButton size="sm" leftIcon={<Play />} disabled={busy} onClick={onRun}>
              {continues
                ? t('workflow:builder.run_continue', { defaultValue: 'Continue with v{{version}}', version: plan.version })
                : t('workflow:builder.run', { defaultValue: 'Run Workflow' })}
            </IntuiButton>
            {fileEnabled && (
              <MethodFilePick label={t('workflow:ask.send_file', { defaultValue: 'Send a method file' })} enabled={!busy} onFile={onFile} />
            )}
          </div>
          {fileEnabled && (
            <p className="mt-2 text-xs text-muted-foreground">
              {t('workflow:ask.send_file_note', {
                defaultValue: 'PDF, Markdown, or plain text, at most 7 MB. Send the file before approval.',
              })}
            </p>
          )}
        </div>
      )}
    </div>
  )
}
